/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {
    IconButton,
    HStack,
    Input,
    useNumberInput
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {MinusIcon, PlusIcon} from '../custom-icons'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'

/**
 * This is the mobile implementation of the Chakra NumberInput. This simple component essentially
 * is a helper so we don't have to reuse the hooks every time we need a number input since design dictates
 * we use the moobile variation on all screens.
 *
 * NOTE: We can optionally put global logic we see if in here, and various styling decisions in this single
 * component.
 *
 * @param {*} props
 * @returns
 */
const QuantityPicker = (props) => {
    const intl = useIntl()
    const productName = props.productName
    const {getInputProps, getIncrementButtonProps, getDecrementButtonProps} = useNumberInput({
        ...props,
        // Defaults
        focusInputOnChange: false,
        onFocus: (e) => {
            const {onFocus} = props

            // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
            // new quantity with one click.
            e.target.select()

            // If there is a `onFocus` property define, call it with the event captured.

            onFocus?.call(this, e)
        }
    })

    const inc = getIncrementButtonProps({
        variant: 'quantityPicker',
        ml: '-1px',
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Increment Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_increment'
            },
            {productName}
        )
    })
    const dec = getDecrementButtonProps({
        variant: 'quantityPicker',
        mr: '-1px',
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Decrement Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_decrement'
            },
            {productName}
        )
    })

    const input = getInputProps({
        maxWidth: '40px',
        width: '40px',
        fontSize: {base: '16px', lg: '12px'},
        textAlign: 'center',
        borderColor: 'borderGray',
        borderRadius: 0,
        'aria-label': intl.formatMessage({
            defaultMessage: 'Quantity:',
            id: 'product_view.label.quantity'
        }),
        _focus: {
            borderWidth: '1px',
            borderColor: 'fullBlack',
            zIndex: 1
        },
        _focusVisible: {
            boxShadow: 'outline'
        }
    })

    // Accessibility improvements:
    // 1. Allow keyboard focus on the buttons - Chakra overrides values passed to get*ButtonProps()
    inc.tabIndex = '0'
    dec.tabIndex = '0'
    // 2. Allow Space or Enter key to trigger buttons
    // Hitting space/enter triggers a "click" event, but the component listens for "mousedown".
    // We can't reuse the buttons' onMouseDown handler, so instead we hijack the input's onKeyDown.
    // @ref https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/react%402.7.0/packages/components/number-input/src/use-number-input.ts#L333-L334
    inc.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.key = 'ArrowUp'
            input.onKeyDown(evt)
        }
    }
    dec.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.key = 'ArrowDown'
            input.onKeyDown(evt)
        }
    }

    return (
        <HStack spacing={0}>
            <IconButton
                data-testid="quantity-decrement"
                icon={<MinusIcon boxSize="12px" />}
                {...dec}
            />
            <Input {...input} />
            <IconButton
                data-testid="quantity-increment"
                icon={<PlusIcon boxSize="12px" />}
                {...inc}
            />
        </HStack>
    )
}

QuantityPicker.propTypes = {
    productName: PropTypes.string,
    onFocus: PropTypes.func
}

export default QuantityPicker
